import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, finalize, firstValueFrom, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/api';
import { CheckoutQuoteResponseV1, CheckoutRequestV1, SquareCheckoutOrchestrationResponseV1 } from '@core/models/order';
import {
  AddPaymentMethodRequestV1,
  PaymentHistorySummaryV1,
  PaymentMethodResponseV1,
  UpdatePaymentMethodBillingAddressRequestV1,
  UpdatePaymentMethodRequestV1,
  WalletResponseV1
} from '@core/models/payment';
import { lazyInject } from '@core/utils/inject';
import { environment } from '@environment/environment';

import { AccountService } from '../account/account';
import { GroupService } from '../group/group';
import { LogService } from '../log/log';
import { ToastService } from '../toast/toast';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);
  private readonly logService = inject(LogService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly groupService = lazyInject(GroupService);

  // Only the signed-in member's wallet is loaded: switching the member being viewed never reads another member's.
  private readonly userAccount = computed(
    () => this.accountService.accountMap()[this.groupService()?.user()?.userId ?? '']
  );

  private readonly baseApiUrl = `${environment.apiUrl}${environment.apis.paymentsEngine}/v1`;

  private readonly walletMap$ = signal<{ [walletId: string]: WalletResponseV1 | undefined }>({});
  readonly walletMap = this.walletMap$.asReadonly();

  // In-flight de-dupe: the wallet effect can fire more than once as accountMap settles at launch, so share
  // one GET per wallet id rather than firing duplicates before walletMap is populated.
  private readonly walletInflight = new Map<string, Observable<WalletResponseV1>>();

  private readonly payments$ = signal<PaymentHistorySummaryV1[]>([]);
  readonly payments = this.payments$.asReadonly();
  readonly paymentsMap = computed(() => {
    const payments = this.payments$();
    const map: { [referenceId: string]: PaymentHistorySummaryV1 | undefined } = {};
    payments.forEach((p) => (map[p.referenceId] = p));
    return map;
  });

  constructor() {
    effect(() => {
      const walletId = this.userAccount()?.walletId;
      untracked(() => {
        if (walletId && !this.walletMap()[walletId]) {
          firstValueFrom(this.getWallet(walletId)).catch(() => {});
        }
      });
    });
  }

  private walletAccountId(walletId: string): string | undefined {
    return (
      this.walletMap()[walletId]?.accountId ??
      Object.values(this.accountService.accountMap()).find((account) => account?.walletId === walletId)?.accountId
    );
  }

  // Throws for a wallet whose account has not loaded, so each caller builds its request inside defer and the
  // throw reaches its own error handling.
  private walletUrl(walletId: string): string {
    const accountId = this.walletAccountId(walletId);
    if (!accountId) throw new Error('The wallet account is required');
    return `${this.baseApiUrl}/accounts/${accountId}/wallet/${walletId}`;
  }

  getWallet(walletId: string): Observable<WalletResponseV1> {
    const accountId = this.walletAccountId(walletId);
    if (!accountId) return throwError(() => new Error('The wallet account is required'));
    const key = `${accountId}/${walletId}`;
    const inflight = this.walletInflight.get(key);
    if (inflight) return inflight;

    this.logService.info('[PaymentService] getWallet', { walletId });
    const request = this.http
      .get<ServiceResponseV1<WalletResponseV1>>(`${this.baseApiUrl}/accounts/${accountId}/wallet`)
      .pipe(
        map((response) => response.data),
        tap((wallet) => {
          this.walletMap$.set({ ...this.walletMap$(), [walletId]: wallet });
        }),
        catchError((error) => this.handleError(error, this.translateService.instant('Payment.Get_Wallet_Error'))),
        finalize(() => this.walletInflight.delete(key)),
        shareReplay(1)
      );
    this.walletInflight.set(key, request);
    return request;
  }

  getPayments(): Observable<PaymentHistorySummaryV1[]> {
    this.logService.info('[PaymentService] getPayments');
    return this.http.get<ServiceResponseV1<PaymentHistorySummaryV1[]>>(`${this.baseApiUrl}/payments`).pipe(
      map((response) => response.data),
      tap((payments) => this.payments$.set(payments)),
      catchError((error) => this.handleError(error, this.translateService.instant('Payment.Get_Payments_Error')))
    );
  }

  getPaymentsByReferenceId(referenceId: string): Observable<PaymentHistorySummaryV1[]> {
    this.logService.info('[PaymentService] getPaymentsByReferenceId', { referenceId });
    const params = new HttpParams({ fromObject: { referenceId } });
    return this.http.get<ServiceResponseV1<PaymentHistorySummaryV1[]>>(`${this.baseApiUrl}/payments`, { params }).pipe(
      map((response) => response.data),
      tap((newPayments) => {
        const existing = this.payments$().filter((p) => p.referenceId !== referenceId);
        this.payments$.set([...existing, ...newPayments]);
      }),
      catchError((error) => this.handleError(error, this.translateService.instant('Payment.Get_Payments_Error')))
    );
  }

  postPaymentmethod(walletId: string, payload: AddPaymentMethodRequestV1): Observable<PaymentMethodResponseV1> {
    this.logService.info('[PaymentService] postPaymentmethod', { walletId, payload });
    return defer(() =>
      this.http.post<ServiceResponseV1<PaymentMethodResponseV1>>(`${this.walletUrl(walletId)}/payment-methods`, payload)
    ).pipe(
      map((response) => response.data),
      tap(() => firstValueFrom(this.getWallet(walletId))),
      catchError((error) => {
        const body = error?.error;
        const providerErrors = body?.data?.errors ?? body?.errors;
        if (providerErrors?.length) {
          return throwError(() => ({ providerErrors }));
        }
        return this.handleError(error, this.translateService.instant('Payment.Add_Payment_Method_Error'));
      })
    );
  }

  updateDefaultPaymentmethod(walletId: string, paymentMethodId: string): Observable<WalletResponseV1> {
    this.logService.info('[PaymentService] updateDefaultPaymentmethod', { walletId, paymentMethodId });
    return defer(() =>
      this.http.put<ServiceResponseV1<WalletResponseV1>>(
        `${this.walletUrl(walletId)}/setDefaultPaymentMethod(${paymentMethodId})`,
        null
      )
    ).pipe(
      map((response) => response.data),
      tap((wallet) => {
        this.walletMap$.set({ ...this.walletMap$(), [walletId]: wallet });
      }),
      catchError((error) =>
        this.handleError(error, this.translateService.instant('Payment.Update_Default_Payment_Method_Error'))
      )
    );
  }

  updatePaymentMethod(
    walletId: string,
    paymentMethodId: string,
    payload: UpdatePaymentMethodRequestV1
  ): Observable<PaymentMethodResponseV1> {
    this.logService.info('[PaymentService] updatePaymentMethod', { walletId, paymentMethodId, payload });
    return defer(() =>
      this.http.patch<ServiceResponseV1<PaymentMethodResponseV1>>(
        `${this.walletUrl(walletId)}/payment-methods/${paymentMethodId}`,
        payload
      )
    ).pipe(
      map((response) => response.data),
      catchError((error) =>
        this.handleError(error, this.translateService.instant('Payment.Update_Payment_Method_Error'))
      )
    );
  }

  updatePaymentMethodBillingAddress(
    walletId: string,
    paymentMethodId: string,
    payload: UpdatePaymentMethodBillingAddressRequestV1
  ): Observable<PaymentMethodResponseV1> {
    this.logService.info('[PaymentService] updatePaymentMethodBillingAddress', { walletId, paymentMethodId, payload });
    return defer(() =>
      this.http.patch<ServiceResponseV1<PaymentMethodResponseV1>>(
        `${this.walletUrl(walletId)}/payment-methods/${paymentMethodId}/billing-address`,
        payload
      )
    ).pipe(
      map((response) => response.data),
      catchError((error) =>
        this.handleError(error, this.translateService.instant('Payment.Update_Payment_Method_Billing_Address_Error'))
      )
    );
  }

  deletePaymentMethod(walletId: string, paymentMethodId: string) {
    this.logService.info('[PaymentService] deletePaymentMethod', { walletId, paymentMethodId });
    return defer(() =>
      this.http.delete<ServiceResponseV1<PaymentMethodResponseV1>>(
        `${this.walletUrl(walletId)}/payment-methods/${paymentMethodId}`,
        { observe: 'response' }
      )
    ).pipe(
      map((response) => {
        if (response.status === 204) {
          return true;
        } else {
          throw new Error('Failed To Delete Payment Method');
        }
      }),
      tap(() => firstValueFrom(this.getWallet(walletId))),
      catchError((error) =>
        this.handleError(error, this.translateService.instant('Payment.Delete_Payment_Method_Error'))
      )
    );
  }

  /**
   * The checkout quote for `orderId`. `groupId` is the household the order belongs to, which the route names and
   * PaymentsEngine refuses an order outside of.
   */
  getQuote(orderId: string, groupId: string | undefined): Observable<CheckoutQuoteResponseV1> {
    this.logService.info('[PaymentService] getQuote', { orderId, groupId });
    const household = groupId;
    if (!household) {
      return this.handleError(
        new Error('No household for checkout'),
        this.translateService.instant('Payment.Get_Order_Totals_Error')
      );
    }
    return this.http
      .get<
        ServiceResponseV1<CheckoutQuoteResponseV1>
      >(`${this.baseApiUrl}/households/${household}/checkout/${orderId}/quote`)
      .pipe(
        map((response) => response.data),
        catchError((error) => this.handleError(error, this.translateService.instant('Payment.Get_Order_Totals_Error')))
      );
  }

  /** Pays for `orderId` in `groupId`, the household it belongs to, as `getQuote` does. */
  postOrderCheckout(
    orderId: string,
    payload: CheckoutRequestV1,
    groupId: string | undefined
  ): Observable<SquareCheckoutOrchestrationResponseV1> {
    this.logService.info('[PaymentService] postOrderCheckout', { orderId, payload, groupId });
    const household = groupId;
    if (!household) {
      return this.handleError(
        new Error('No household for checkout'),
        this.translateService.instant('Payment.Post_Order_Checkout_Error')
      );
    }
    return this.http
      .post<SquareCheckoutOrchestrationResponseV1>(
        `${this.baseApiUrl}/households/${household}/checkout/${orderId}`,
        payload
      )
      .pipe(
        catchError((error) => {
          const body = error?.error as SquareCheckoutOrchestrationResponseV1;
          if (body?.errors?.length) {
            return of(body);
          }
          return this.handleError(error, this.translateService.instant('Payment.Post_Order_Checkout_Error'));
        })
      );
  }

  private handleError(error: any, message: string) {
    const header = this.translateService.instant('Common.Something_Went_Wrong');
    this.logService.error('[PaymentService] Error', { error, message });
    this.toastService.error(header, message);
    return throwError(() => error);
  }
}
